<template>
  <div v-if="filtersGroupAll && filtersGroupAll.length">
    <UiExpandedBlock class="block w-full md:hidden" :class="blockClasses">
      <template #activator="{ attrs, open }">
        <button
          v-bind="attrs"
          class="flex w-full items-center justify-between text-base font-250 uppercase"
        >
          <span class="text-left">
            {{ $t("sortBy") }}
            <span class="font-normal">({{ selectedSort.label }})</span>
          </span>
          <UiIcon
            :name="open ? 'chevron_up' : 'chevron_down'"
            class="pointer-events-none size-6 flex-shrink-0 text-orange-60"
          />
        </button>
      </template>
      <template #content>
        <div class="flex flex-col gap-2 py-2.5">
          <UiRadio
            v-for="option in sortOptions"
            :key="option.value"
            v-model="sort"
            name="sortBy"
            :label="option.label"
            :value="option.value"
          />
        </div>
      </template>
    </UiExpandedBlock>
    <UiExpandedBlock
      v-if="minMaxData && price.length"
      :title="$t('price')"
      class="w-full"
      :class="blockClasses"
    >
      <template #activator="{ attrs, open }">
        <button
          v-bind="attrs"
          class="flex w-full items-center justify-between text-base font-250 uppercase"
        >
          <span>
            {{ $t("price") }}
          </span>
          <UiIcon
            :name="open ? 'chevron_up' : 'chevron_down'"
            class="pointer-events-none size-6 flex-shrink-0 text-orange-60"
          />
        </button>
      </template>
      <template #content>
        <div class="flex flex-col-reverse gap-2 py-2.5">
          <div
            class="flex items-center justify-between text-xs font-light text-primary"
          >
            <span>
              {{ $n(minMaxData.cost_min, "currency") }}
            </span>
            <span>
              {{ $n(minMaxData.cost_max, "currency") }}
            </span>
          </div>
          <UiRange
            v-slot="{ setHandle }"
            v-model="price"
            :min="minMaxData.cost_min"
            :max="minMaxData.cost_max"
            @update="onUpdate"
          >
            <div class="flex items-center justify-between text-gray-60">
              <UiCurrencyInput
                ref="minRef"
                :model-value="price[0]"
                class="max-w-34 [&_input]:text-center [&_input]:font-light"
                :min="minMaxData.cost_min"
                :max="price[1]"
                @update:model-value="setHandle($event, 0)"
              />
              —
              <UiCurrencyInput
                ref="maxRef"
                :model-value="price[1]"
                class="max-w-34 [&_input]:text-center [&_input]:font-light"
                :min="price[0]"
                :max="minMaxData.cost_max"
                @update:model-value="setHandle($event, 1)"
              />
            </div>
          </UiRange>
        </div>
      </template>
    </UiExpandedBlock>
    <UiExpandedBlock
      v-for="(filter, idx) in filtersGroupAll"
      :key="idx"
      :title="filter.currentTranslation.title"
      open
      class="w-full"
      :class="blockClasses"
    >
      <template #activator="{ attrs, open }">
        <button
          v-bind="attrs"
          class="flex w-full items-center justify-between text-base font-250 uppercase"
          :data-e2e-item="dataE2eFilterCategory(filter)"
        >
          <span>
            {{ filter.currentTranslation.title }}
            {{
              tagsByCategory[filter.id]?.length
                ? ` (${tagsByCategory[filter.id].length})`
                : ""
            }}
          </span>
          <UiIcon
            :name="open ? 'chevron_up' : 'chevron_down'"
            class="pointer-events-none size-6 flex-shrink-0 text-orange-60"
          />
        </button>
      </template>
      <template #content>
        <div
          class="styled-scrollbars max-h-[204px] space-y-6 overflow-auto py-2.5"
        >
          <div v-for="(option, optionIdx) in filter.value" :key="optionIdx">
            <UiCheckbox
              :model-value="filterQuery[filter.key]"
              :true-value="option.value"
              :value="option.value"
              false-value=""
              :name="option.currentTranslation?.title"
              class="!block h-full cursor-pointer text-sm [&_label]:cursor-pointer [&_label]:text-text-secondary"
              hide-details
              :data-e2e-item="dataE2eFilterItem(option)"
              @update:modelValue="updateQuery($event, filter.key)"
            >
              <template #label>
                <span class="flex items-center gap-1 text-xs font-light">
                  <UiLazyImg
                    v-if="getImageByPosition(option.medias, 'list')?.file?.url"
                    class="size-4 overflow-hidden rounded-full"
                    :src="
                      getImageByPosition(option.medias, 'list')?.file?.url ||
                      '/images/no-image.webp'
                    "
                    :alt="
                      getImageByPosition(option.medias, 'list')?.file?.alt ||
                      option.currentTranslation?.title
                    "
                  />
                  {{ option.currentTranslation?.title }}
                </span>
              </template>
            </UiCheckbox>
          </div>
        </div>
      </template>
    </UiExpandedBlock>
  </div>
</template>

<script lang="ts" setup>
import { RangesData } from "ecom-types";
import { useCatalogFilters } from "~/composables";

const props = withDefaults(
  defineProps<{
    collectionIds?: number[];
    categoryIds?: number[];
    hideFilterType?: string[];
    showAll?: boolean;
    isEngagement?: boolean;
    show_collections_filters?: boolean;
    show_categories_filters?: boolean;
    blockClasses?: string;
    minMaxData?: RangesData;
  }>(),
  {
    isEngagement: false,
    show_collections_filters: false,
    show_categories_filters: false,
  },
);

const route = useRoute();
const router = useRouter();

const minRef = ref();
const maxRef = ref();

const {
  filterQuery,
  price,
  filtersGroupAll,
  tagsByCategory,
  sortOptions,
  sortBy,
} = useCatalogFilters({
  isEngagement: props.isEngagement,
  minMaxData: props.minMaxData,
  categoryIds: props.categoryIds,
  collectionIds: props.collectionIds,
  showCollectionsFilters: props.show_collections_filters,
  showCategoriesFilters: props.show_categories_filters,
  showAll: props.showAll,
});

const selectedSort = computed(() => {
  return (
    sortOptions.find((option) => option.value === sortBy.value.value) ||
    sortOptions[0]
  );
});

const sort = computed({
  get() {
    return sortBy.value.value;
  },
  set(value) {
    sortBy.value =
      sortOptions.find((option) => option.value === value) || sortOptions[0];
  },
});

function onUpdate(e: [number, number]) {
  const minUnref = unrefElement(minRef);
  const maxUnref = unrefElement(maxRef);

  if (minUnref && maxUnref) {
    minUnref.querySelector("input").value = e[0].toFixed(2);
    maxUnref.querySelector("input").value = e[1].toFixed(2);
  }
}

// watch(
//   // TODO remove when it will be possible to set default opened tab in admin
//   filtersGroupAll,
//   (val) => {
//     const filterWithStyleInSkuIdx = val.findIndex((el) =>
//       el?.slug?.includes("style"),
//     );
//     const filterWithCategoriesKeyIdx = val.findIndex(
//       (el) => el?.key === "categories",
//     );
//     const filterWithCollectionsKeyIdx = val.findIndex(
//       (el) => el?.key === "collections",
//     );
//     if (filterWithCategoriesKeyIdx > 0 || filterWithCategoriesKeyIdx === 0) {
//       openedIdx.value = filterWithCategoriesKeyIdx;
//     } else if (
//       filterWithCollectionsKeyIdx > 0 ||
//       filterWithCollectionsKeyIdx === 0
//     ) {
//       openedIdx.value = filterWithCollectionsKeyIdx;
//     } else if (filterWithStyleInSkuIdx > 0 || filterWithStyleInSkuIdx === 0) {
//       openedIdx.value = filterWithStyleInSkuIdx;
//     } else if (!val[0]?.sku) {
//       if (val?.length > 1) {
//         openedIdx.value = 1;
//       } else {
//         openedIdx.value = 0;
//       }
//     }
//   },
//   {
//     immediate: true,
//   },
// );

function updateQuery(value: string[], key: string) {
  router.push({ query: { ...route.query, page: 1, [key]: value } });
}

function dataE2eFilterCategory(category) {
  return `${category.sku}-filter-group`;
}

function dataE2eFilterItem(filter) {
  return `${filter.sku}-filter`;
}
</script>
